<template lang="pug">
.jackpotCounter.whitespace-nowrap
  span.inline-block {{ displayValue }}
  span {{ config.public.currency }}
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

import { useRuntimeConfig } from '#app'
import { useFormat } from '@/composables/core/useFormat'

const props = defineProps({
  jackpot: {
    type: Object,
    required: true,
  },
  isJackpotUnstart: {
    type: Boolean,
    default: false,
  },
})

// 動畫配置常數
const ANIMATION_STEPS = 4
const ANIMATION_CONFIG = {
  STEP_RATIOS: [0.9, 0.07, 0.02], // 最後一步自動計算到目標值
  DURATIONS: [3, 6, 16, 35], // 每個步驟的持續時間（秒）
  INITIAL_DURATION: 1, // 重置時的持續時間
  MAX_STEP_INDEX: 3, // 最大步驟索引
  DEBOUNCE_DELAY: 100, // watch 防抖延遲（毫秒）
}

const config = useRuntimeConfig()
const { getFormatted } = useFormat()
const jackpotTarget = ref(0)
const currentStep = ref(0)
const counterConfigs = ref(
  Array(ANIMATION_STEPS)
    .fill(null)
    .map(() => ({
      from: 0,
      to: 0,
      duration: 0,
    }))
)
const displayValue = ref('0.00')
const animationFrameId = ref(null)
let debounceTimer = null

/**
 * 計算當前 jackpot 獎勵金額
 * @returns {number} 獎勵金額，如果未開始則返回 0
 */
const jackpotReward = computed(() => {
  // 檢查 jackpot 是否已開始
  if (props.isJackpotUnstart || !props.jackpot?.jackpotStart) {
    return 0
  }

  const { totalReward = 0, rewardConfig } = props.jackpot
  const defaultAmount = rewardConfig?.defaultAmount ?? 0

  // 返回總獎勵與預設金額的較大值
  return Math.max(totalReward, defaultAmount)
})

/**
 * 格式化數字為貨幣顯示格式
 * @param {number} val - 要格式化的數值
 * @returns {string} 格式化後的字串
 */
const counterFormat = (val) => {
  return getFormatted(val, 'FORCE_FLOAT')
}

/**
 * 緩動函數：二次緩入緩出 (easeInOutQuad)
 * 創造平滑的動畫效果，開始和結束時較慢，中間較快
 * @param {number} t - 進度值 (0-1)
 * @returns {number} 緩動後的進度值
 */
const easeInOutQuad = (t) => {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}

/**
 * 使用 requestAnimationFrame 執行數字動畫
 * @param {number} from - 起始值
 * @param {number} to - 目標值
 * @param {number} duration - 持續時間（秒）
 * @param {Function} callback - 動畫完成後的回調函數
 */
const animateNumber = (from, to, duration, callback) => {
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value)
    animationFrameId.value = null
  }

  const startTime = performance.now()
  const durationMs = duration * 1000

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / durationMs, 1)
    const easedProgress = easeInOutQuad(progress)
    const currentValue = from + (to - from) * easedProgress

    displayValue.value = counterFormat(currentValue)

    if (progress < 1) {
      animationFrameId.value = requestAnimationFrame(animate)
    } else {
      displayValue.value = counterFormat(to)
      animationFrameId.value = null
      if (callback) callback()
    }
  }

  animationFrameId.value = requestAnimationFrame(animate)
}

/**
 * 停止當前正在執行的動畫
 */
const killAnimation = () => {
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value)
    animationFrameId.value = null
  }
}

/**
 * 啟動當前步驟的動畫
 */
const startAnimation = () => {
  const config = counterConfigs.value[currentStep.value]
  if (config) {
    animateNumber(config.from, config.to, config.duration, onComplete)
  }
}

/**
 * 計算動畫的各階段目標值
 * @param {number} startValue - 起始值
 * @param {number} endValue - 結束值
 * @returns {Array<{from: number, to: number, duration: number}>} 動畫配置陣列
 */
const calculateAnimationSteps = (startValue, endValue) => {
  const diff = endValue - startValue
  let currentValue = startValue

  return ANIMATION_CONFIG.STEP_RATIOS.map((ratio, index) => {
    const from = currentValue
    const to = currentValue + diff * ratio
    currentValue = to

    return {
      from,
      to,
      duration: ANIMATION_CONFIG.DURATIONS[index],
    }
  }).concat([
    {
      from: currentValue,
      to: endValue,
      duration: ANIMATION_CONFIG.DURATIONS[ANIMATION_CONFIG.STEP_RATIOS.length],
    },
  ])
}

/**
 * 更新動畫配置，複用現有陣列避免創建新物件
 * @param {Array<{from: number, to: number, duration: number}>} steps - 計算好的動畫步驟
 */
const updateAnimationConfigs = (steps) => {
  steps.forEach((step, index) => {
    counterConfigs.value[index].from = step.from
    counterConfigs.value[index].to = step.to
    counterConfigs.value[index].duration = step.duration
  })
}

/**
 * 處理 jackpot 值小於當前顯示值的情況
 */
const handleValueDecrease = () => {
  jackpotTarget.value = jackpotReward.value
  counterConfigs.value[0].from = 0
  counterConfigs.value[0].to = 0
  counterConfigs.value[0].duration = ANIMATION_CONFIG.INITIAL_DURATION
  displayValue.value = counterFormat(0)
}

/**
 * 重置動畫狀態並計算新的動畫步驟
 */
const resetAnimation = () => {
  currentStep.value = 0

  // jackpot 值比顯示小，就是要從 0 開始
  if (jackpotReward.value < jackpotTarget.value) {
    handleValueDecrease()
    return
  }

  // 計算並更新動畫步驟
  const steps = calculateAnimationSteps(jackpotTarget.value, jackpotReward.value)
  updateAnimationConfigs(steps)

  startAnimation()
}

/**
 * 檢查是否需要重置動畫（值未變化時）
 * @returns {boolean} 是否需要重置
 */
const shouldResetAnimation = () => {
  return currentStep.value === 0 && jackpotReward.value === jackpotTarget.value
}

/**
 * 檢查是否有下一個動畫步驟
 * @returns {boolean} 是否有下一步
 */
const hasNextStep = () => {
  return currentStep.value < ANIMATION_CONFIG.MAX_STEP_INDEX
}

/**
 * 執行下一個動畫步驟
 */
const executeNextStep = () => {
  currentStep.value++
  jackpotTarget.value = counterConfigs.value[currentStep.value].to
  startAnimation()
}

/**
 * 動畫步驟完成時的回調函數
 * 處理下一步驟的執行或重置邏輯
 */
const onComplete = () => {
  if (shouldResetAnimation()) {
    jackpotTarget.value = 0
    resetAnimation()
    return
  }

  if (hasNextStep()) {
    executeNextStep()
  }
}

/**
 * 監聽 jackpot 獎勵變化，使用 debounce 避免頻繁重置
 */
watch(jackpotReward, (newVal, oldVal) => {
  // 清除之前的 debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }

  // 使用 debounce 延遲執行
  debounceTimer = setTimeout(() => {
    killAnimation()
    resetAnimation()
    debounceTimer = null
  }, ANIMATION_CONFIG.DEBOUNCE_DELAY)
})

onMounted(() => {
  resetAnimation()
})

onBeforeUnmount(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  killAnimation()
})
</script>

<style lang="scss"></style>
